/**
 * Embed widget operational issues — the browser-hosted chat door.
 *
 * ## Why `PROVIDER` and not a new category
 *
 * `ISSUE_CATEGORY` names the SUBSYSTEM, and `fault` names who must act. The far
 * side of an embed is the customer's own page and the server that signs for its
 * visitors, which is the same relationship every channel registry here already
 * files under `PROVIDER` — and `SANDBOX_ISSUES` is the standing precedent for a
 * `PROVIDER` code whose far side is not a messaging vendor at all. Adding a
 * category member for one code is a taxonomy change that reaches an exhaustive
 * `Record<IssueCategory, …>` in the admin UI.
 *
 * **That revisit was owed at the second code and has now happened: still
 * `PROVIDER`, and the trigger is sharpened rather than met.** Two codes about
 * one subsystem is a count, not a reason — a category earns its place when an
 * operator needs to FILTER embed issues apart from channel issues on the admin
 * Issues page, and with these two both scoped to a single deployment id the
 * `entityType`/`entityId` filters already answer every question anyone has
 * asked. Revisit when a code lands here whose entity is NOT a deployment, or
 * when the admin page grows an embed-only view; a bare third code is not the
 * trigger either.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const EMBED_ISSUES = {
  /**
   * The deployment declares identity verification ON and carries no usable
   * signing secret, so no hash can ever be right and every visitor the
   * customer's site identifies is refused.
   *
   * 🔴 **CUSTOMER-FACING ON PURPOSE, AND THE REASON IS THAT IT CANNOT BE
   * FORGED.** It is raised from `POST /public/v1/deployments/:id/chat-session/anonymous`,
   * which is `@AllowUnauthenticated()` — so a stranger can make us NOTICE this
   * condition, by claiming an identity, but cannot CREATE it: the condition is
   * a property of the stored deployment row and is true or false whether or not
   * anybody calls. Its sibling identity refusals (`invalid`, `missing-hash`, a
   * hash sent to a deployment with verification off) are all trivially
   * producible by anyone holding the deployment id, so they raise NO issue at
   * all and reach the operator log line only. That split is the whole security
   * argument for putting this one in front of a customer.
   *
   * Dedupe is per-deployment (`entityType: "deployment"`), so an id sweep grows
   * no rows — it only refreshes `count` and `lastSeenAt` on a row whose
   * underlying condition is real.
   */
  EMBED_IDENTITY_VERIFICATION_MISCONFIGURED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "Embed identity verification is enabled on this deployment with no usable signing secret, so every visitor the customer's site identifies is refused with 403",
    severity: "high",
    remedy:
      "The deployment's embed settings have `identityVerificationEnabled: true` and an absent or invalid `identityVerificationSecret` (it must be 32-64 characters). Open the deployment in the console, go to Embed > Security > Identity verification, and press Generate New Secret; the customer must then sign each visitor id with that secret and send the result as the widget's `data-user-hash`. Turning identity verification off is the other valid repair and makes every visitor anonymous.",
    fault: ISSUE_FAULT.CONFIG,
    userTitle: "Identity verification is not finished",
    userGuidance:
      "This widget has identity verification switched on but has no signing secret, so visitors your site identifies are being turned away and chat with the agent anonymously or not at all. Open the deployment's Embed settings, Security section, and generate a signing secret — then sign each visitor's id with it. If you did not mean to switch identity verification on, turn it off instead."
  },

  /**
   * The deployment restricts its widget to named domains, and the allowlist
   * matches no origin the widget we serve can ever present — so the embed
   * refuses every visitor on the owner's own page.
   *
   * 🔴 **CUSTOMER-FACING FOR THE SAME REASON ITS SIBLING ABOVE IS: THE
   * CONDITION IS UNFORGEABLE.** Both operands are server-side — the stored
   * `securitySettings`, and the frontend origin this process serves the embed
   * iframe from. A stranger holding the deployment id can make us EVALUATE it,
   * by sending a request that gets refused, but cannot CREATE it: it is true or
   * false whether or not anybody calls. The origin the refused request carried
   * is never read, never compared and never recorded — a detector keyed on it
   * would be reporting a condition the caller chose, which is precisely the
   * split that keeps the four sibling identity refusals unreported.
   *
   * **The refusal it explains is deliberately silent, which is why this exists.**
   * `EmbedAuthGuard` answers an off-allowlist origin with the same
   * `deploymentNotFound` an absent or private deployment gives, so the guard
   * leaks no configuration oracle — and therefore cannot tell the owner why
   * their own widget stopped working. This code is the one surface allowed to
   * say it, because it is scoped to the tenant who owns the row.
   *
   * ⚠️ **It reports the IFRAME path only.** A deployment whose live traffic is
   * the direct `/embedded-message` door is compared against a real host-page
   * `Origin` there, where the same allowlist means exactly what the console
   * promises — so an allowlist naming a customer domain is correct on that door
   * and unsatisfiable on this one. Raising this from the point of an actual
   * door-1 refusal is what keeps the two apart: a deployment nobody loads
   * through the iframe never reports.
   *
   * Dedupe is per-deployment (`entityType: "deployment"`), so an id sweep grows
   * no rows — it only refreshes `count` and `lastSeenAt` on a row whose
   * underlying condition is real.
   *
   * Resolved by `ReconcileUnsatisfiableEmbedAllowlistIssuesUseCase`, hourly, once
   * the deployment's stored allowlist can admit the served origin again or the
   * deployment is gone. The reporter cannot do it: a fixed deployment is no
   * longer refused, so it never reaches the reporter again.
   */
  EMBED_DOMAIN_ALLOWLIST_UNSATISFIABLE: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "This deployment restricts its embed to specific domains and the allowlist matches none of the origins the widget we serve can present, so every visitor on the owner's own page is refused",
    severity: "high",
    remedy:
      "The deployment's embed settings have `securitySettings.allowOnlyOnSpecificDomains: true` with an `allowedDomains` list that cannot match the origin the embed iframe runs on. The pasted `<script>` snippet injects an iframe served from FRONTEND_BASE_URL, so on that path the browser's `Origin` is the Nexus frontend host and never the customer's website — an allowlist naming the customer's own domain therefore refuses every request, and the guard answers it with the same `deploymentNotFound` an absent deployment gives, so the owner sees nothing. Two valid repairs: switch `allowOnlyOnSpecificDomains` off in the console (Embed > Security), which restores the widget immediately; or, if the restriction is wanted, keep it and move that deployment onto a door where the host page's own origin is what gets compared. The `rawPayload` on this row names the origins that were compared, which is the fastest way to confirm the diagnosis.",
    fault: ISSUE_FAULT.CONFIG,
    userTitle: "This widget's domain restriction blocks every visitor",
    userGuidance:
      'You have restricted this chat widget to specific domains, but the list cannot match where the widget actually runs, so every visitor — including on your own website — is being turned away with no error shown. Open the deployment\'s Embed settings, Security section, and switch "Allow only on specific domains" off to bring the widget back. If you need the restriction, contact us before turning it back on so we can set it up on a path where it works.'
  }
} as const satisfies Record<string, IssueDefinition>;
